import { useEffect, useRef } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

/**
 * Submits the form after every field edit — the permission grids save on selection rather than
 * through a save button. Saves are serialised: while one is in flight, further edits queue
 * exactly one follow-up, and the follow-up reads the form at send time — so a burst of edits
 * collapses into its final state, and two full-map writes cannot land out of order. Only real
 * field input starts a save: programmatic resets and query-driven reseeds carry no `change`
 * event type, so settling the form after a save cannot start the next one.
 */
export function useFormAutosave<T extends FieldValues>(
	methods: UseFormReturn<T>,
	submit: () => Promise<void>,
) {
	// The freshest submit closure, so a queued follow-up never saves through a stale render.
	const submitRef = useRef(submit);
	submitRef.current = submit;
	const isInFlight = useRef(false);
	const isQueued = useRef(false);

	useEffect(() => {
		async function run(): Promise<void> {
			if (isInFlight.current) {
				isQueued.current = true;
				return;
			}
			isInFlight.current = true;
			try {
				await submitRef.current();
			} finally {
				isInFlight.current = false;
				if (isQueued.current) {
					isQueued.current = false;
					void run();
				}
			}
		}
		const subscription = methods.watch((_values, info) => {
			if (info.type !== "change") return;
			void run();
		});
		return () => subscription.unsubscribe();
	}, [methods]);

	return {
		/** Whether an edit arrived while the current save was still in flight. */
		hasQueuedSave: () => isQueued.current,
	};
}
